// AppLayout.tsx — каркас: sidebar (бренд + tenant-switcher + навигация + футер) и
// <Outlet>. Навигация фильтруется по capability (Ф8 RBAC): пункт виден, только
// если у роли есть право (сервер всё равно проверяет). Tenant-switcher — для
// owner'а с несколькими tenant'ами; переключение переиздаёт сессию.

import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { api } from '../api/client';
import type { Capability } from '../api/client';
import type { TenantSummary } from '../api/contract';
import { useSession } from '../auth/session';
import { initials } from '../lib/format';

interface NavItem {
  to: string;
  icon: string;
  label: string;
  end?: boolean;
  cap: Capability;
}

const NAV: NavItem[] = [
  { to: '/', icon: '📊', label: 'Сегодня', end: true, cap: 'viewDashboard' },
  { to: '/week', icon: '🗓️', label: 'Неделя', cap: 'viewDashboard' },
  { to: '/report', icon: '📄', label: 'Отчёт за день', cap: 'viewDashboard' },
  { to: '/analytics', icon: '📈', label: 'Аналитика', cap: 'analytics' },
  { to: '/builder', icon: '🧩', label: 'Конструктор анкет', cap: 'editSurveys' },
  { to: '/schedule', icon: '⏰', label: 'Расписание', cap: 'editSchedule' },
  { to: '/access', icon: '🔐', label: 'Роли и доступы', cap: 'manageAccess' },
  { to: '/manage', icon: '⚙️', label: 'Управление', cap: 'manageRespondents' },
];

const ROLE_LABEL: Record<string, string> = {
  owner: 'владелец',
  director: 'директор',
  manager: 'менеджер',
  observer: 'наблюдатель',
};

export default function AppLayout() {
  const { me, logout, can } = useSession();
  const nav = NAV.filter((item) => can(item.cap));

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="brand">
          <div className="logo">TG</div>
          <div style={{ flex: 1 }}>
            <b>TG-oprosnik</b>
            <small>панель отчётов</small>
          </div>
        </div>

        {can('manageTenants') && <TenantSwitcher />}

        <nav className="nav">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => (isActive ? 'active' : undefined)}
            >
              <span className="ico">{item.icon}</span> {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="side-foot">
          <div className="who">
            <div className="avatar">{me ? initials(me.name) : '—'}</div>
            <div style={{ flex: 1 }}>
              {me?.name ?? '—'}
              <br />
              <span style={{ color: 'var(--muted)', fontSize: 11 }}>
                {me ? (ROLE_LABEL[me.webRole] ?? me.webRole) : ''}
              </span>
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <button
              type="button"
              className="btn ghost"
              style={{ width: '100%', padding: 7 }}
              onClick={() => void logout()}
            >
              Выйти
            </button>
          </div>
        </div>
      </aside>

      <main className="main">
        <Outlet />
      </main>
    </div>
  );
}

// Переключатель активного tenant'а (только owner). Грузит список один раз; если
// tenant'ов >1 — раскрывает меню. Переключение переиздаёт сессию → reload, чтобы
// все экраны перечитали данные нового tenant'а.
function TenantSwitcher() {
  const [tenants, setTenants] = useState<TenantSummary[] | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .access()
      .then((a) => {
        if (alive) setTenants(a.tenants);
      })
      .catch(() => {
        if (alive) setTenants([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  const current = tenants?.find((t) => t.isCurrent);
  if (!current) return null;

  const switchable = (tenants ?? []).filter((t) => !t.isCurrent);

  async function pick(tenantId: string) {
    setBusy(true);
    try {
      await api.switchTenant(tenantId);
      window.location.assign('/'); // новая сессия → перечитать всё
    } finally {
      setBusy(false);
      setOpen(false);
    }
  }

  return (
    <div className="tenant-switch">
      <button
        type="button"
        className="brand tenant"
        onClick={() => switchable.length > 0 && setOpen((v) => !v)}
        style={{ cursor: switchable.length > 0 ? 'pointer' : 'default', width: '100%' }}
      >
        <div className="logo" style={{ width: 30, height: 30, fontSize: 12 }}>
          {initials(current.title)}
        </div>
        <div style={{ flex: 1, textAlign: 'left' }}>
          <b style={{ fontSize: 13 }}>{current.title}</b>
          <small>
            {current.respondentCount} чел · {current.surveyCount} анкет
          </small>
        </div>
        {switchable.length > 0 && <span style={{ color: 'var(--muted)' }}>▾</span>}
      </button>
      {open && (
        <div className="tmenu open">
          {switchable.map((t) => (
            <button
              key={t.id}
              type="button"
              className="rrow"
              disabled={busy}
              onClick={() => void pick(t.id)}
            >
              <div className="logo" style={{ width: 26, height: 26, fontSize: 11 }}>
                {initials(t.title)}
              </div>
              <div className="nm">
                <b>{t.title}</b>
                <small>
                  {t.respondentCount} чел · {t.surveyCount} анкет
                </small>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
